import React, { useState } from 'react'
import { Button } from '@sureco/design-system'
import { message } from 'antd'
import { CensusFileRowGridData } from '../types/census-file-row-grid-data'
import { CensusFileRowDto } from '../api/dto/census-file-row.dto'
import { updateCensusFileRows } from '../api/census-enrollment-platform-api-client'
import { CensusFileRowStatusEnum } from '../api/enums/census-file-row-status.enum'
import { mapGridDataToCensusFileRowDto } from '../utils/map-grid-data-to-census-file-row-dto'
import { PatchActionEnum } from '../api/enums/patch-action.enum'
import SendForReviewConfirmationModal from './SendForReviewConfirmationModal'

const DUPLICATE_IDENTIFIER_FIELDS = [
  'SSN',
  'EmailAddress',
  'CompanyEmployeeId',
] as const

const hasDuplicateIdentifierError = (row: CensusFileRowGridData): boolean => {
  if (!row.errors) return false
  return DUPLICATE_IDENTIFIER_FIELDS.some((field) =>
    row.errors?.[field]?.errorMessage
      ?.toLowerCase()
      .includes('rows with identical')
  )
}

interface SendSelectedForReviewButtonProps {
  publicKey: string
  selectedRows: CensusFileRowGridData[]
  handleSubmit: (rows: CensusFileRowGridData[]) => void
  cleanAllRows: () => void
  setLoading: (loading: boolean) => void
  skipConfirmModal?: boolean
}

const sendForReview = async (
  rowsToSubmit: CensusFileRowGridData[],
  publicKey: string
) => {
  const acceptedRows: CensusFileRowDto[] = rowsToSubmit.map((gridRow) => ({
    ...mapGridDataToCensusFileRowDto(gridRow),
    status: CensusFileRowStatusEnum.IN_REVIEW,
  }))
  await updateCensusFileRows(
    acceptedRows,
    publicKey,
    PatchActionEnum.SUBMIT_FOR_REVIEW
  )
}

const SendSelectedForReviewButton = ({
  selectedRows,
  publicKey,
  handleSubmit,
  cleanAllRows,
  setLoading,
  skipConfirmModal = false,
}: SendSelectedForReviewButtonProps) => {
  const [loading, setButtonLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)

  const validRows = selectedRows.filter(
    (r) => !r.isNewEmployee && !hasDuplicateIdentifierError(r)
  )
  const excludedRows = selectedRows.filter(
    (r) => r.isNewEmployee || hasDuplicateIdentifierError(r)
  )

  const handleConfirm = async (rows: CensusFileRowGridData[]) => {
    let timer: NodeJS.Timeout | null = null
    let shouldShowLoading = false

    timer = setTimeout(() => {
      setButtonLoading(true)
      setLoading(true)
      shouldShowLoading = true
    }, 800)

    try {
      await sendForReview(rows, publicKey)
      message.success(`${rows.length} records submitted successfully`, 3)
      setModalVisible(false)
    } catch (error) {
      message.error(`Failed to submit records: '${(error as Error).message}'`)
    } finally {
      if (shouldShowLoading) {
        setButtonLoading(false)
        setLoading(false)
      }
      if (timer) {
        clearTimeout(timer)
      }
      handleSubmit(rows)
      cleanAllRows()
    }
  }

  if (selectedRows.length === 0) return null

  return (
    <>
      <Button
        onClick={() =>
          skipConfirmModal ? handleConfirm(selectedRows) : setModalVisible(true)
        }
        size="large"
        loading={loading}
      >
        {loading ? 'Submitting...' : `Send For Review (${selectedRows.length})`}
      </Button>
      <SendForReviewConfirmationModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onConfirm={() => handleConfirm(validRows)}
        loading={loading}
        validRows={validRows}
        excludedRows={excludedRows}
      />
    </>
  )
}

export default SendSelectedForReviewButton
